import { getEnemyDefs, enemyByKind } from "./enemy-defs.js";
/**
 * Pure targeting logic for the RuneScape-style interaction model. These
 * functions decide what a click DOES from the latest observation — the client
 * never decides outcomes, only intentions (the server is authoritative on
 * everything). Pure over Observation so they're unit-testable without a DOM.
 *
 * Interaction rules (design canon):
 * - Left click on an object (key, floor stone, locked door, boss/portal): walk to a
 *   standable tile adjacent to it, then interact. Already adjacent → interact
 *   immediately.
 * - Left click on an open door: "Enter" — jump to the far-side tile. The
 *   server either pathfinds through (far room already revealed) or takes the
 *   open-door threshold jump (far room unseen — stepping through the doorway
 *   reveals it on arrival).
 * - Left click on a live enemy (or within its body radius): attack it — a
 *   standing order the sim chases and auto-attacks (the server is the only
 *   one that decides whether the hit lands).
 * - Left click on reachable floor: walk there (standing order).
 * - Left click on anything else (wall/void/locked-off floor): walk to the
 *   nearest revealed floor tile (BFS from the click point).
 */
import { combatLevelTone, consumableById, itemByKind, npcDisplayLevel, FLIP_TILES_SIDE, flipTileTarget } from "@pd/shared";
import type { CombatLevelTone, CombatTables, NpcDef, Observation, ObservedEnemy, PuzzleState } from "@pd/shared";

/** The display name of a (class, kind) world/pocket item — the shared
 *  catalogs resolve each half (design/loot.md: a world item is a pair).
 *  Null when the catalog has no entry (the sim never produces one). */
export function lootItemName(cls: "equipment" | "consumable", kind: number): string | null {
  if (cls === "equipment") return itemByKind(kind)?.name ?? null;
  return consumableById(kind)?.name ?? null;
}

export interface Vec2 {
  /** Cosmetic sprite elevation in native pixels; absent for ground positions. */
  height?: number;
  x: number;
  y: number;
}

export type WorldObject =
  | { kind: "chasmTile"; id: number; x: number; y: number; state: number }
  | { kind: "iceTile"; x: number; y: number; pad: number; active: boolean; blocked: boolean }
  | { kind: "wizardOrb"; id: number; x: number; y: number }
  | { kind: "puzzleTile"; id: number; x: number; y: number; solved: boolean; forceDamage: number; force?: boolean }
  | { kind: "key"; id: number; x: number; y: number }
  | { kind: "stone"; id: number; x: number; y: number; stoneKind: "personal" | "group"; ownerId: number }
  | { kind: "door"; id: number; x: number; y: number; open: boolean; keyId: number; guarded: boolean; puzzleKind?: PuzzleState["kind"]; puzzleLocked?: boolean; axis: "h" | "v" }
  // The floor's exit: the boss block while the boss monster lives
  // (Interact is rejected), the exit portal once it is dead (Interact enters
  // — the host's ends the floor, anyone else's votes, run.md).
  // Only exists while state is "portal" (boss dead) — while the boss LIVES
  // the tile is plain floor (objectAt/objectsAt skip it), so the boss NPC
  // on that tile keeps its own Attack/Examine menu and nothing in the boss
  // room offers "Enter" until the kill (the playtest: a living boss with an
  // "Enter" verb reads as a bug).
  | { kind: "boss"; id: number; x: number; y: number; state: "portal" }
  // The return portal (run.md): the base room's way back to the hub. Ends
  // YOUR floor only (the party keeps playing without you); your pocket (keys,
  // carried items) is left on the floor, your worn gear comes with you.
  | { kind: "returnPortal"; x: number; y: number }
  // A combat target — dungeons spawn NPCs (0-5 per room, the boss room's one
  // boss monster) and the arena spawns its fixture mobs; both resolvers
  // yield this.
  | { kind: "enemy"; id: number; x: number; y: number }
  // A dead enemy's corpse — a floor object you can stand on and right-click
  // (Examine / Walk here). A corpse tile that carries a drop pile resolves
  // to the pile instead (objectAt finds the drops first) — the loot is the
  // point of the tile (design/loot.md).
  | { kind: "corpse"; id: number; x: number; y: number; npcKind: string }
  // A GROUND DROP (design/loot.md): one item of a pile on its tile. `id`
  // names the SPECIFIC item (Interact "item" targetId) — left-click sends
  // the pile's top (highest id on the tile), the right-click menu sends the
  // chosen one; cls/itemKind carry the catalog pair for the labels (the
  // pocket is class-tagged, so the name needs both halves).
  | { kind: "item"; id: number; x: number; y: number; cls: "equipment" | "consumable"; itemKind: number }
  // The base-room SACK (design/loot.md). Primary click opens the centered
  // inventory-style modal; right-click uses the normal object menu.
  // `itemId` names the SPECIFIC sack item to withdraw (Interact "sack" targetId),
  // while the tile-level object leaves it -1.
  | { kind: "sack"; x: number; y: number; itemId: number };

export type PrimaryAction =
  | { kind: "interact"; label: string; obj: WorldObject }
  // The sack's left-click verb opens its inventory-style item modal.
  | { kind: "openSack"; label: string; x: number; y: number }
  | { kind: "walk"; label: string; x: number; y: number }
  | { kind: "enter"; label: string; door: WorldObject & { kind: "door" } }
  // The combat verb (both the game and the arena yield it). `obj` carries the
  // enemy so the click feedback can flash its silhouette (the interact marker).
  | { kind: "attack"; label: string; target: number; x: number; y: number;
      obj: WorldObject & { kind: "enemy" } }
  | { kind: "self" }
  | { kind: "none" };

const DIRS: ReadonlyArray<readonly [number, number]> = [
  [0, 1],
  [1, 0],
  [0, -1],
  [-1, 0],
];

export function chebyshev(a: Vec2, b: Vec2): number {
  return Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y));
}

/** How close (Chebyshev, tiles) a click can be to a live enemy and still
 *  count as clicking IT. 0 = the click must land ON the enemy's tile.
 *
 *  History: this used to be a whole-room hitbox, then was tightened to 1
 *  tile ("the body rises a tile above its foot tile"). In play, 1 tile was
 *  still too generous — a click on any of the 8 tiles AROUND an enemy routed
 *  to Attack, and because a (non-stackable) enemy blocks its own tile while
 *  awake, the hero often could not even path to that adjacent tile, so the
 *  click felt like a solid wall you couldn't get past. 0 makes the enemy's
 *  clickbox its own tile: point AT the creature to attack, point at
 *  anywhere else to walk. Stacked (stackable) enemies on one tile resolve
 *  to the lowest id (deterministic tie-break below).
 *
 *  LAYER ABOVE THIS (2026-08-27): the shared Viewport's sprite hit-test
 *  (hitbox.ts) — a click on a live enemy's rendered BODY snaps to its tile
 *  before this resolver runs, so a 256 px dragon is targetable by its wings
 *  and neck, not just its foot tile. This 0 stays the pure-tile rule; the
 *  body is the input layer's body-radius. Dead enemies have no sprite — the
 *  corpse stays tile-only (corpseNear, same radius). */
export const ENEMY_CLICK_RADIUS = 0;

/** The live enemy within ENEMY_CLICK_RADIUS of (x, y) — nearest Chebyshev,
 *  lowest id breaks ties — or null. Pure over Observation; the game's and
 *  the arena's resolvers both route left- and right-clicks through this. */
export function enemyNear(obs: Observation, x: number, y: number): ObservedEnemy | null {
  return enemyNearWith(obs, x, y, true);
}

/** The DEAD enemy within ENEMY_CLICK_RADIUS of (x, y) — the corpse, a floor
 *  object (right-click: Examine / Walk here; left-click walks onto it). Same
 *  radius as the live enemy: the pile sits on the death tile, so a click on
 *  that tile targets the corpse. */
export function corpseNear(obs: Observation, x: number, y: number): ObservedEnemy | null {
  return enemyNearWith(obs, x, y, false);
}

function enemyNearWith(obs: Observation, x: number, y: number, alive: boolean): ObservedEnemy | null {
  let best: ObservedEnemy | null = null;
  for (const e of obs.enemies) {
    if (e.alive !== alive) continue;
    const d = chebyshev(e, { x, y });
    if (d > ENEMY_CLICK_RADIUS) continue;
    if (best === null || d < chebyshev(best, { x, y }) || (d === chebyshev(best, { x, y }) && e.id < best.id)) {
      best = e;
    }
  }
  return best;
}

/** Revealed, passable floor — what the server will let you stand on. Doors
 *  are thresholds, never standing tiles: their tiles look like floor in the
 *  map but are not walkable (locked or open). */
export function isWalkable(obs: Observation, x: number, y: number): boolean {
  if (x < 0 || y < 0 || x >= obs.width || y >= obs.height) return false;
  if (obs.revealed[y * obs.width + x] !== 1) return false;
  if (obs.map[y * obs.width + x] !== 1) return false;
  return !obs.doors.some((d) => d.x === x && d.y === y);
}

export function puzzleTileAt(obs: Observation, x: number, y: number): Extract<WorldObject, { kind: "puzzleTile" }> | null {
  const p = obs.puzzles?.find((p) => p.kind === "flip-tiles" && x >= p.x && y >= p.y && x < p.x + FLIP_TILES_SIDE && y < p.y + FLIP_TILES_SIDE);
  return p?.kind === "flip-tiles" ? { kind: "puzzleTile", id: flipTileTarget(p.id, (y - p.y) * FLIP_TILES_SIDE + x - p.x), x, y, solved: p.solved, forceDamage: p.forceDamage } : null;
}

export function chasmTileAt(obs: Observation, x: number, y: number): Extract<WorldObject, { kind: "chasmTile" }> | null {
  const p = obs.puzzles?.find((p) => p.kind === "chasm-crossing" && x >= p.x && y >= p.y && x < p.x + p.side && y < p.y + p.side);
  if (p?.kind !== "chasm-crossing") return null;
  const i = (y - p.y) * p.side + x - p.x;
  return p.normalTiles.includes(i) ? null : { kind: "chasmTile", id: y * obs.width + x, x, y, state: p.tiles[i]! };
}

export function iceTileAt(obs: Observation, x: number, y: number): Extract<WorldObject, { kind: "iceTile" }> | null {
  const p = obs.puzzles?.find((p) => p.kind === "icy-pressure-pads" && x >= p.x && y >= p.y && x < p.x + p.side && y < p.y + p.side);
  if (p?.kind !== "icy-pressure-pads") return null;
  const index = (y - p.y) * p.side + x - p.x;
  if (p.safeTiles.includes(index)) return null;
  const pad = p.pads.findIndex((pad) => pad.index === index);
  return { kind: "iceTile", x, y, pad, active: pad >= 0 && p.pads[pad]!.active, blocked: p.obstacles.includes(index) };
}

export function wizardOrbAt(obs: Observation, x: number, y: number): Extract<WorldObject, { kind: "wizardOrb" }> | null {
  for (const enemy of obs.enemies) {
    const orb = enemy.alive ? enemy.orbWave?.orbs.find((orb) => orb.x === x && orb.y === y) : undefined;
    if (orb) return { kind: "wizardOrb", ...orb };
  }
  return null;
}

export function objectAt(obs: Observation, x: number, y: number): WorldObject | null {
  const orb = wizardOrbAt(obs, x, y);
  if (orb) return orb;
  const key = obs.keys.find((k) => k.x === x && k.y === y);
  if (key) return { kind: "key", id: key.id, x, y };
  const stone = obs.stones.find((s) => s.state === "floor" && s.x === x && s.y === y);
  if (stone) return { kind: "stone", id: stone.id, x, y, stoneKind: stone.kind, ownerId: stone.ownerId };
  const sack = obs.sack;
  if (sack && sack.x === x && sack.y === y) return { kind: "sack", x, y, itemId: -1 };
  // The pile's TOP (highest id on the tile) is the single left-click target
  // (design/loot.md: left-click takes the top). Ties are impossible — ids
  // are unique; the scan is a max, not a find-first.
  let top: WorldObject & { kind: "item" } | null = null;
  for (const d of obs.drops) {
    if (d.x !== x || d.y !== y) continue;
    if (!top || d.id > top.id) top = { kind: "item", id: d.id, x, y, cls: d.cls, itemKind: d.kind };
  }
  if (top) return top;
  const door = obs.doors.find((d) => d.x === x && d.y === y);
  if (door) return { kind: "door", id: door.id, x, y, open: door.open, keyId: door.keyId, guarded: door.guarded, puzzleLocked: door.puzzleLocked, puzzleKind: door.puzzleKind, axis: door.axis };
  const boss = obs.boss;
  if (boss && boss.state === "portal" && boss.x === x && boss.y === y) return { kind: "boss", id: boss.id, x, y, state: boss.state };
  const rp = obs.returnPortal;
  if (rp && rp.x === x && rp.y === y) return { kind: "returnPortal", x, y };
  return puzzleTileAt(obs, x, y) ?? iceTileAt(obs, x, y) ?? chasmTileAt(obs, x, y);
}

/** ALL objects on a tile (stacked keys and stones share a tile now): every
 *  floor key, floor stone, then the door and boss if present. Order is
 *  deterministic — keys, stones, door, boss — matching `objectAt` priority. */
export function objectsAt(obs: Observation, x: number, y: number): WorldObject[] {
  const out: WorldObject[] = [];
  for (const k of obs.keys) {
    if (k.x === x && k.y === y) out.push({ kind: "key", id: k.id, x, y });
  }
  for (const s of obs.stones) {
    if (s.state === "floor" && s.x === x && s.y === y) {
      out.push({ kind: "stone", id: s.id, x, y, stoneKind: s.kind, ownerId: s.ownerId });
    }
  }
  const sack = obs.sack;
  if (sack && sack.x === x && sack.y === y) out.push({ kind: "sack", x, y, itemId: -1 });
  // EVERY drop on the tile (the pile), TOP FIRST — highest id first
  // (design/loot.md: the context menu lists every item in the pile, top
  // first). obs.drops is id-ascending (spawn order), so a descending scan.
  const here = obs.drops.filter((d) => d.x === x && d.y === y);
  for (let i = here.length - 1; i >= 0; i--) {
    const d = here[i]!;
    out.push({ kind: "item", id: d.id, x, y, cls: d.cls, itemKind: d.kind });
  }
  const door = obs.doors.find((d) => d.x === x && d.y === y);
  if (door) out.push({ kind: "door", id: door.id, x, y, open: door.open, keyId: door.keyId, guarded: door.guarded, puzzleLocked: door.puzzleLocked, puzzleKind: door.puzzleKind, axis: door.axis });
  const boss = obs.boss;
  if (boss && boss.state === "portal" && boss.x === x && boss.y === y) out.push({ kind: "boss", id: boss.id, x, y, state: boss.state });
  const rp = obs.returnPortal;
  if (rp && rp.x === x && rp.y === y) out.push({ kind: "returnPortal", x, y });
  const orb = wizardOrbAt(obs, x, y);
  if (orb) out.unshift(orb);
  const puzzle = puzzleTileAt(obs, x, y);
  if (puzzle) out.push(puzzle);
  const chasm = chasmTileAt(obs, x, y);
  if (chasm) out.push(chasm);
  const ice = iceTileAt(obs, x, y);
  if (ice) out.push(ice);
  return out;
}

export function objectTitle(obj: WorldObject, playerName?: (id: number) => string): string {
  switch (obj.kind) {
    case "chasmTile":
      return obj.state === 0 ? "Unrevealed tile" : obj.state === 1 ? "Chasm" : "Safe tile";
    case "iceTile":
      return obj.blocked ? "Ice boulder" : obj.pad >= 0 ? `Pressure pad ${obj.pad + 1}${obj.active ? " (active)" : ""}` : "Ice";
    case "wizardOrb":
      return "Orb";
    case "puzzleTile":
      return "Flip tile";
    case "key":
      return `Key ${keyLetter(obj.id)}`;
    case "stone": {
      if (obj.stoneKind === "group") return "Group gatestone";
      return `Gatestone (${playerName?.(obj.ownerId) ?? "?"})`;
    }
    case "door":
      return obj.open ? "Doorway" : obj.puzzleLocked ? "Puzzle door" : obj.guarded ? "Guardian door" : `Door (key ${keyLetter(obj.keyId)})`;
    case "boss":
      return "The exit portal"; // only exists once the boss is dead
    case "returnPortal":
      return "The return portal";
    case "item":
      return lootItemName(obj.cls, obj.itemKind) ?? "An item";
    case "sack":
      return "Sack";
    case "enemy":
      return "Enemy";
    case "corpse":
      return `${enemyByKind(obj.npcKind)?.name ?? "Something"} bones`;
  }
}

function primaryLabel(obj: WorldObject): string {
  switch (obj.kind) {
    case "wizardOrb": return "Burst orb";
    case "chasmTile":
      return "Inspect tile";
    case "iceTile":
      return "Slide";
    case "puzzleTile":
      return obj.force ? "Force tile" : "Imbue tile";
    case "key":
      return "Take key";
    case "stone":
      return "Take gatestone";
    case "door":
      return "Open door";
    case "boss":
      return "Enter"; // the open portal (the host's verb is re-labelled in game.ts)
    case "returnPortal":
      return "Leave the floor"; // the base room's way home (run.md)
    case "item":
      return "Take"; // the top of the pile — the label is re-composed with the name in the menu/readout
    case "sack":
      return "Open sack"; // the item list (design/loot.md)
    case "enemy":
      return "Attack";
    // Unreachable in practice — a corpse is never a primary target (objectAt
    // doesn't yield one); the case keeps the switch exhaustive.
    case "corpse":
      return "Examine";
  }
}

/**
 * What a left click at (x, y) does, from this player's observation.
 * `me` is this player's ObservedPlayer (position only is used).
 */
export function primaryAction(obs: Observation, me: Vec2, x: number, y: number): PrimaryAction {
  // Objects first: standing ON a key/stone tile must still offer pickup — a
  // self-click on your own tile would otherwise swallow the take (the classic
  // "I'm standing on the item, walk off to pick it up" trap).
  const obj = objectAt(obs, x, y);
  if (obj) {
    if (obj.kind === "chasmTile") return groundAction(obs, me, x, y);
    if (obj.kind === "iceTile") {
      const action = groundAction(obs, me, x, y);
      return action.kind === "walk" ? { ...action, label: "Slide here" } : action;
    }
    if (obj.kind === "puzzleTile" && obj.solved) return groundAction(obs, me, x, y);
    if (obj.kind === "door" && obj.open) {
      return { kind: "enter", label: "Enter", door: obj };
    }
    // The sack's left click is the item list, not an immediate interact
    // (design/loot.md): open the context menu at the cursor — the same menu
    // the right click gets (its Take entries approach + withdraw).
    if (obj.kind === "sack") {
      return { kind: "openSack", label: "Open sack", x, y };
    }
    return {
      kind: "interact",
      // A drop's label names the item ("Take Loot Helm") — objectTitle
      // carries the name; every other object keeps its verb label.
      label: obj.kind === "item" ? `Take ${objectTitle(obj)}` : primaryLabel(obj),
      obj,
    };
  }
  // Tile-only callers retain the old object-first rule. Screen-space floor
  // clicks resolve a visible body before reaching this tile resolver.
  const enemy = enemyNear(obs, x, y);
  if (enemy) {
    return {
      kind: "attack",
      label: `Attack the ${enemyByKind(enemy.kind)?.name ?? "enemy"}`,
      target: enemy.id,
      x: enemy.x,
      y: enemy.y,
      obj: { kind: "enemy", id: enemy.id, x: enemy.x, y: enemy.y },
    };
  }
  return groundAction(obs, me, x, y);
}

/** Ground intent after sprite and object picking. A click in the bare part of
 * an NPC's foot tile is still a ground click, not an attack. */
export function groundAction(obs: Observation, me: Vec2, x: number, y: number): PrimaryAction {
  if (me.x === x && me.y === y) return { kind: "self" };
  if (isWalkable(obs, x, y)) return { kind: "walk", label: "Walk here", x, y };
  const near = nearestRevealedFloor(obs, x, y);
  if (near) return { kind: "walk", label: "Walk here", x: near.x, y: near.y };
  return { kind: "none" };
}

/**
 * The tile to walk to before interacting with an object, plus whether the
 * player will actually be able to interact once there.
 *
 * Keys and stones (floor items) prefer walking ON TOP of the item, then to
 * the nearest standable adjacent tile, then — if the item is walled off —
 * to the nearest revealed floor (the closest possible tile), marked
 * `reachable: false` so the caller shows REASON.cantReach on arrival
 * instead of sending a doomed Interact.
 *
 * Doors and boss keep the nearest-standable behaviour (a door's own tile is
 * never a standing tile; the boss's is), with equal-distance ties broken by
 * lateral offset to the door so the approach walk stays a straight line.
 */
export interface Approach {
  tile: Vec2;
  reachable: boolean;
}

export function interactApproach(obs: Observation, me: Vec2, obj: WorldObject): Approach {
  if (obj.kind === "wizardOrb") return { tile: { x: me.x, y: me.y }, reachable: true };
  if (chebyshev(me, obj) <= 1) return { tile: { x: me.x, y: me.y }, reachable: true };

  // Floor items (keys, stones, drops, the sack): prefer standing on the item,
  // unless a live NPC occupies it. Pickup only requires adjacency, so walking
  // beside that NPC reaches the item without asking the sim to route through
  // an occupied tile.
  if (obj.kind === "key" || obj.kind === "stone" || obj.kind === "item" || obj.kind === "sack") {
    // (a) walk on top of the item
    const occupied = (x: number, y: number): boolean =>
      obs.enemies.some((enemy) => enemy.alive && enemy.x === x && enemy.y === y);
    if (isWalkable(obs, obj.x, obj.y) && (chasmTileAt(obs, obj.x, obj.y)?.state ?? 2) === 2 && !occupied(obj.x, obj.y)) {
      return { tile: { x: obj.x, y: obj.y }, reachable: true };
    }
    // (b) nearest standable neighbour
    const neighbor = nearestStandable(obs, me, standableCandidates(obj).filter((tile) => !occupied(tile.x, tile.y)));
    if (neighbor) return { tile: neighbor, reachable: true };
    // (c) as close as possible, then error on arrival
    const near = nearestRevealedFloor(obs, obj.x, obj.y);
    if (near) return { tile: near, reachable: false };
    return { tile: { x: me.x, y: me.y }, reachable: false };
  }

  // door / boss: nearest-standable, tie-broken by LATERAL offset (the axis
  // perpendicular to the door's strip) so a hero in line with the door walks
  // straight to the in-line tile instead of drifting one row to the side.
  const perp = obj.kind === "door" ? (obj.axis === "h" ? "y" : "x") : undefined;
  const best = nearestStandable(obs, me, standableCandidates(obj), perp);
  if (best) return { tile: best, reachable: true };
  return { tile: { x: me.x, y: me.y }, reachable: false };
}

/** 8 neighbours + (for keys/stones/boss, not doors) the object's own tile. */
function standableCandidates(obj: WorldObject): Vec2[] {
  const candidates: Vec2[] = [];
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      if (dx === 0 && dy === 0) continue;
      candidates.push({ x: obj.x + dx, y: obj.y + dy });
    }
  }
  if (obj.kind !== "door") candidates.push({ x: obj.x, y: obj.y });
  return candidates;
}

/**
 * The standable candidate closest to me (Chebyshev), or null. Ties break on
 * `perp` — the candidate's offset along the axis perpendicular to the door's
 * strip — so when several approach tiles are equally close (the usual case:
 * the hero is far along the door's axis), the one that keeps the walk a
 * straight line wins instead of the first candidate in iteration order (which
 * was biased north). A final (x, y) total order makes the result
 * deterministic and independent of candidate order.
 */
function nearestStandable(obs: Observation, me: Vec2, candidates: Vec2[], perp?: "x" | "y"): Vec2 | null {
  let best: Vec2 | null = null;
  let bestScore: [number, number, number, number] | null = null;
  for (const c of candidates) {
    if (!isWalkable(obs, c.x, c.y) || (chasmTileAt(obs, c.x, c.y)?.state ?? 2) !== 2) continue;
    const d = chebyshev(me, c);
    const lateral = perp === "x" ? Math.abs(c.x - me.x) : perp === "y" ? Math.abs(c.y - me.y) : 0;
    const score: [number, number, number, number] = [d, lateral, c.x, c.y];
    if (
      bestScore === null ||
      score[0] < bestScore[0] ||
      (score[0] === bestScore[0] &&
        (score[1] < bestScore[1] ||
          (score[1] === bestScore[1] &&
            (score[2] < bestScore[2] || (score[2] === bestScore[2] && score[3] < bestScore[3])))))
    ) {
      best = c;
      bestScore = score;
    }
  }
  return best;
}

/**
 * "Enter" through an open door: the far-side room tile (the side tile farther
 * from me along the door's axis). The client never decides legality — it just
 * aims for the far side and the server walks or threshold-jumps. When I'm
 * equidistant (standing on the doorway) the deterministic first candidate
 * wins; the server accepts either side.
 */
export function enterTarget(me: Vec2, door: WorldObject & { kind: "door" }): Vec2 {
  const candidates: Vec2[] =
    door.axis === "h"
      ? [
          { x: door.x - 1, y: door.y },
          { x: door.x + 1, y: door.y },
        ]
      : [
          { x: door.x, y: door.y - 1 },
          { x: door.x, y: door.y + 1 },
        ];
  return candidates.reduce((far, c) => (chebyshev(me, c) > chebyshev(me, far) ? c : far));
}

/**
 * BFS from (x, y) for the nearest revealed floor tile (excluding locked door
 * tiles — those read as floor in the map but can't be walked onto). The
 * "walk to the closest tile" rule for unclickable targets.
 */
export function nearestRevealedFloor(obs: Observation, x: number, y: number): Vec2 | null {
  const seen = new Set<string>([`${x},${y}`]);
  const queue: Vec2[] = [{ x, y }];
  while (queue.length > 0) {
    const cur = queue.shift()!;
    for (const [dx, dy] of DIRS) {
      const nx = cur.x + dx;
      const ny = cur.y + dy;
      const k = `${nx},${ny}`;
      if (seen.has(k)) continue;
      seen.add(k);
      if (nx < 0 || ny < 0 || nx >= obs.width || ny >= obs.height) continue;
      if (isWalkable(obs, nx, ny)) return { x: nx, y: ny };
      queue.push({ x: nx, y: ny });
    }
  }
  return null;
}

export function keyLetter(keyId: number): string {
  return String.fromCharCode(65 + keyId);
}

// ---- the attack verb's level tag (cosmetic, shared by the game + arena) ----

/**
 * The enemy's displayed combat level: the store's `combatLevel` override when
 * the human authored one, otherwise DERIVED from the entry's stats (shared
 * `npcDisplayLevel`). The stats come from the committed catalog like every
 * other client-side NPC lookup; the accuracy contest and bare-hero rows the
 * derivation needs come from the RUN's table (`obs.combat`), so a dev-server
 * knob edit retunes the readout on the next floor without a client rebuild.
 * Null for a kind the catalog doesn't know — no number beats a wrong one.
 *
 * There is no hero combat level to compare against: the number is a threat
 * ordinal and the tone below is what makes it legible.
 */
export function enemyCombatLevel(
  e: ObservedEnemy,
  tables: CombatTables,
  /** The catalog to resolve the kind in — the committed one in the game (like
   *   every client-side NPC lookup); an explicit one in tests, which must never
   *   pin what a human happens to have authored in npc.store (AGENTS §12). */
  catalog: readonly NpcDef[] = getEnemyDefs(),
): number | null {
  return npcDisplayLevel(catalog.find((d) => d.kind === e.kind), tables);
}

/** The level tag's colour band — THE shared thresholds (green below 35,
 *  yellow below 60, red above), so the menu, the arena and any future surface
 *  agree on what a given number means. */
export type LevelTone = CombatLevelTone;

/** A colored trailing tag for a menu item (the attack verb's "(level 162)").
 *  Rendered as a styled span so labels stay plain text (no HTML). */
export interface LevelTag {
  text: string;
  tone: LevelTone;
}

/** The attack verb's level tag, or null when the enemy's kind resolves to no
 *  level at all. Pure over the enemy + the run's combat table. */
export function levelTag(
  e: ObservedEnemy,
  tables: CombatTables,
  catalog: readonly NpcDef[] = getEnemyDefs(),
): LevelTag | null {
  const lvl = enemyCombatLevel(e, tables, catalog);
  if (lvl === null) return null;
  // the leading space is the separator — the tag's raw text reads
  // "Attack: Luminak (level 162)" when concatenated, and the styled span
  // carries it (white-space: pre), so copy-paste matches the visuals.
  return { text: ` (level ${lvl})`, tone: combatLevelTone(lvl) };
}

/**
 * The attack verb's menu label + tag: "Attack: Luminak (level 162)" — the
 * name carries the stack count for stackables, the tag carries the level.
 * One builder for the game's and the arena's context menus so they can't
 * drift (the left-click/hover readout keeps its "Attack the X" form).
 */
export function attackLabel(
  name: string,
  e: ObservedEnemy,
  tables: CombatTables,
  catalog: readonly NpcDef[] = getEnemyDefs(),
): { label: string; tag: LevelTag | null } {
  return { label: `Attack: ${name}`, tag: levelTag(e, tables, catalog) };
}

/** Stable per-player color (HSL), shared by renderer, HUD, chat, and map. */
export function playerColor(id: number): string {
  return `hsl(${(id * 137.508) % 360}, 65%, 55%)`;
}

/** The same identity colour as an hsla() string, for translucent glows.
 *  Shares the hue with playerColor so a personal gatestone's radiance and
 *  its owner's name tag/map marker are one colour. */
export function playerGlow(id: number, alpha: number): string {
  return `hsla(${(id * 137.508) % 360}, 65%, 55%, ${alpha})`;
}
